import type { RefObject } from 'react';

import type {
  ExpressWalletEligibility,
  ExpressWalletPresentation,
  ExpressWalletReadinessProps,
} from '@/lib/expressWalletReadiness';
import type { ApiError, ApiErrorType } from '@/lib/types';

export interface PaypalPayer {
  address: {
    country_code: string;
  };
  email_address: string;
  name:
    | {
        given_name: string;
        surname: string;
      }
    | undefined;
  phone:
    | {
        phone_number:
          | {
              national_number: string;
            }
          | undefined;
        phone_type: string;
      }
    | undefined;
}

export interface PaypalExpressButtonProps extends ExpressWalletReadinessProps {
  areBraceletSizeErrorsSet: () => boolean;
  errorHandler?: (
    errors: ApiError[],
    opts?: { errorType?: ApiErrorType },
  ) => void;
  isExpressCheckoutBlocked?: boolean;
  zIndex?: number;
}

export interface UsePaypalExpressReturn {
  eligibility: ExpressWalletEligibility;
  isExpressPaymentAllowed: boolean;
  isGatewayConfigLoading: boolean;
  isPaypalExpressActivated: boolean;
  isPaypalExpressAvailable: boolean;
  isTransactionSuccessfully: boolean;
  paypalButtonRef: RefObject<HTMLDivElement>;
  presentation: ExpressWalletPresentation;
  providerGeneration: object;
}

export interface PaypalShippingAddress {
  city?: string;
  country?: string;
  firstName?: string;
  houseNumberOrName?: string;
  lastname?: string;
  postalCode?: string;
  stateOrProvince?: string;
  street?: string;
}

export interface PaypalAuthorizedData {
  authorizedEvent?: {
    payer?: PaypalPayer;
  };
  billingAddress?: PaypalShippingAddress;
  deliveryAddress?: PaypalShippingAddress;
}

export interface PaypalAuthorizedActions {
  reject: () => void;
  resolve: () => void;
}

export interface PaypalShippingChangeData {
  shippingAddress: {
    city: string;
    countryCode: string;
    postalCode: string;
    state: string;
  };
}

export interface PaypalShippingChangeActions {
  reject: () => void;
  resolve: () => void;
}

export interface PaypalComponent {
  paymentData: string;
  updatePaymentData: (paymentData: string) => void;
}

export interface FieldError {
  code?: string;
  field?: string;
  message?: string;
}

export const isFieldError = (error: unknown): error is FieldError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    ('field' in error || 'code' in error)
  );
};

export const isPhoneInvalidError = (error: unknown): boolean => {
  return (
    isFieldError(error) && error.field === 'phone' && error.code === 'INVALID'
  );
};

export interface UIElementWithReject {
  handleReject: (status: string, message: string) => void;
}

export const hasHandleReject = (el: unknown): el is UIElementWithReject => {
  return (
    typeof el === 'object' &&
    el !== null &&
    'handleReject' in el &&
    typeof (el as UIElementWithReject).handleReject === 'function'
  );
};

export const rejectPayment = (
  el: unknown,
  status: string,
  message: string,
): void => {
  if (hasHandleReject(el)) {
    el.handleReject(status, message);
  }
};
